import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

// Bundle-tiers PDP widget (Stage 1: UI only).
// Tier cards (1/2/3 units) with per-unit engraving text fields. On Add to
// cart it adds one line item per engraved unit (each carrying its own
// properties[Engraving]) via /cart/add.js with an items array, then opens
// the cart drawer. All units use the CURRENTLY selected variant, so any
// engraving upcharge in the picker applies to every unit. When it
// initializes it takes over the buy flow: the native buy-buttons and any
// in-section lm-engraving block are hidden. If this module fails to load
// (e.g. a locked-down in-app browser), those native controls stay visible
// and usable — no dead end.

const CART_ADD_URL = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
const CART_URL = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';

if (!(window.customElements && customElements.get('lm-bundle-tiers'))) {
  class LmBundleTiers extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currency = this.dataset.currency || 'USD';
      this.showClaims = this.dataset.showClaims === 'true';
      this.currentVariantId = this.dataset.defaultVariantId || '';

      this.tiers = Array.from(this.querySelectorAll('[data-lm-tier]'));
      this.radios = Array.from(this.querySelectorAll('[data-lm-tier-radio]'));
      this.engraveRows = Array.from(this.querySelectorAll('[data-lm-engrave-row]'));
      this.addButton = this.querySelector('[data-lm-bundle-add]');
      this.errorEl = this.querySelector('[data-lm-bundle-error]');

      // Without the core controls there is nothing to enhance — bail and
      // leave the native buy flow untouched.
      if (!this.addButton || !this.radios.length) return;

      this.prices = {};
      const priceScript = this.querySelector('[data-lm-bundle-prices]');
      if (priceScript) {
        try {
          this.prices = JSON.parse(priceScript.textContent) || {};
        } catch (e) {
          this.prices = {};
        }
      }

      this.sectionEl = document.getElementById('shopify-section-' + this.sectionId);
      this.scope = this.sectionEl || document;
      this.variantPicker = this.findVariantPicker();
      this.syncCurrentVariant();

      // Bundle owns the buy flow: hide the native add-to-cart + engraving.
      this.hideNativeControls();

      this._onTierChange = this.onTierChange.bind(this);
      this.radios.forEach((r) => r.addEventListener('change', this._onTierChange));

      this._onEngraveInput = this.onEngraveInput.bind(this);
      this.engraveRows.forEach((row) => {
        const input = row.querySelector('[data-lm-engrave-input]');
        if (input) input.addEventListener('input', this._onEngraveInput);
      });

      this._onAdd = this.onAdd.bind(this);
      this.addButton.addEventListener('click', this._onAdd);

      // Recompute prices when the shopper switches variant.
      this._onVariantUpdate = this.onVariantUpdate.bind(this);
      document.addEventListener('variant:update', this._onVariantUpdate);

      this.updateRows();
      this.recomputePrices();

      // Reveal our button now that JS is live.
      this.addButton.removeAttribute('hidden');
    }

    disconnectedCallback() {
      if (this._onTierChange) this.radios.forEach((r) => r.removeEventListener('change', this._onTierChange));
      if (this._onEngraveInput) {
        this.engraveRows.forEach((row) => {
          const input = row.querySelector('[data-lm-engrave-input]');
          if (input) input.removeEventListener('input', this._onEngraveInput);
        });
      }
      if (this.addButton && this._onAdd) this.addButton.removeEventListener('click', this._onAdd);
      if (this._onVariantUpdate) document.removeEventListener('variant:update', this._onVariantUpdate);
    }

    findVariantPicker() {
      const pickers = document.querySelectorAll('variant-picker');
      for (let i = 0; i < pickers.length; i++) {
        if (pickers[i].dataset.sectionId === this.sectionId) return pickers[i];
      }
      return pickers[0] || null;
    }

    syncCurrentVariant() {
      let id = null;
      if (this.variantPicker && this.variantPicker.selectedOption) {
        id = this.variantPicker.selectedOption.dataset.variantId;
      }
      if (!id) {
        const input = this.scope.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
        if (input) id = input.value;
      }
      if (id) this.currentVariantId = String(id);
    }

    hideNativeControls() {
      // Native add-to-cart: hide the product-form-component wrapping the
      // buy-buttons form in this section (covers add-to-cart, quantity and
      // dynamic checkout). Fall back to the form element itself.
      const form = this.scope.querySelector('form[id^="BuyButtons-ProductForm-"]');
      if (form) {
        const wrapper = form.closest('product-form-component') || form;
        wrapper.style.display = 'none';
        this._hiddenNative = wrapper;
      }
      // Competing engraving UI: hide the in-section lm-engraving block.
      const eng =
        this.scope.querySelector('lm-engraving[data-section-id="' + this.sectionId + '"]') ||
        this.scope.querySelector('lm-engraving');
      if (eng) {
        eng.style.display = 'none';
        this._hiddenEngraving = eng;
      }
    }

    selectedUnits() {
      const checked = this.radios.find((r) => r.checked) || this.radios[0];
      const units = parseInt(checked && checked.value, 10);
      return units > 0 ? units : 1;
    }

    onTierChange() {
      this.clearError();
      this.updateRows();
    }

    onEngraveInput(event) {
      const row = event.target.closest('[data-lm-engrave-row]');
      if (!row) return;
      const remaining = row.querySelector('[data-lm-engrave-remaining]');
      if (!remaining) return;
      const max = parseInt(event.target.getAttribute('maxlength'), 10) || 20;
      let left = max - event.target.value.length;
      if (left < 0) left = 0;
      remaining.textContent = String(left);
    }

    onVariantUpdate(event) {
      const id = event && event.detail && event.detail.resource && event.detail.resource.id;
      if (id) {
        this.currentVariantId = String(id);
      } else {
        this.syncCurrentVariant();
      }
      this.recomputePrices();
    }

    // Show one engraving field per unit of the selected tier.
    updateRows() {
      const units = this.selectedUnits();
      this.engraveRows.forEach((row) => {
        const idx = parseInt(row.dataset.unitIndex, 10) || 0;
        if (idx > 0 && idx <= units) row.removeAttribute('hidden');
        else row.setAttribute('hidden', '');
      });
    }

    recomputePrices() {
      const unitPrice = this.prices[this.currentVariantId];
      if (typeof unitPrice !== 'number') return; // leave server-rendered values

      this.tiers.forEach((tier) => {
        const units = parseInt(tier.dataset.units, 10) || 0;
        const percent = parseInt(tier.dataset.percent, 10) || 0;
        if (units < 1) return;

        const total = unitPrice * units;
        const totalEl = tier.querySelector('[data-lm-tier-total]');
        const compareEl = tier.querySelector('[data-lm-tier-compare]');

        if (this.showClaims && percent > 0) {
          const discounted = Math.round((total * (100 - percent)) / 100);
          if (compareEl) compareEl.textContent = this.money(total);
          if (totalEl) totalEl.textContent = this.money(discounted);
        } else if (totalEl) {
          totalEl.textContent = this.money(total);
        }
      });
    }

    money(cents) {
      try {
        return formatMoney(cents, this.moneyFormat, this.currency);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    buildItems() {
      const units = this.selectedUnits();
      const id = Number(this.currentVariantId);
      const items = [];
      for (let i = 1; i <= units; i++) {
        const item = { id: id, quantity: 1 };
        const row = this.engraveRows.find((r) => parseInt(r.dataset.unitIndex, 10) === i);
        if (row) {
          const input = row.querySelector('[data-lm-engrave-input]');
          const text = input && input.value.trim();
          // Each engraved unit carries its own property; blank units omit it
          // (Shopify merges identical blank lines into one).
          if (text) item.properties = { Engraving: text };
        }
        items.push(item);
      }
      return items;
    }

    // Read the ticked lm-product-addons rows straight from the DOM (the same stable
    // data-attribute contract lm-addons.js uses) so this still works if lm-addons.js never
    // upgraded the element in a locked-down in-app browser. Mirrors
    // LmProductAddons.checkedItems(): same variant source + _addon line-item properties.
    collectAddonItems() {
      const el = document.querySelector('lm-product-addons');
      if (!el) return [];
      const handle = el.dataset.mainHandle || '';
      const items = [];
      el.querySelectorAll('[data-lm-addon-row]').forEach((row) => {
        const check = row.querySelector('[data-lm-addon-check]');
        if (!check || !check.checked) return;
        const id = Number(row.dataset.variantId);
        if (!id) return;
        items.push({ id: id, quantity: 1, properties: { _addon: 'true', _addon_for: handle } });
      });
      return items;
    }

    // Untick the add-ons after a successful add so a second bundle ATC doesn't re-add them
    // (parity with lm-addons.js clearChecks() after the native flow).
    clearAddonChecks() {
      const el = document.querySelector('lm-product-addons');
      if (!el) return;
      el.querySelectorAll('[data-lm-addon-check]:checked').forEach((c) => {
        c.checked = false;
      });
    }

    gatherSectionIds() {
      const ids = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
      });
      return ids.join(',');
    }

    onAdd() {
      if (this._busy) return;
      this.clearError();

      const id = Number(this.currentVariantId);
      if (!id) {
        this.showError('Please choose your options first.');
        return;
      }

      const items = this.buildItems();
      if (!items.length) return;

      // Fold any ticked "Make it complete" add-ons into the SAME cart/add.js call so the
      // bundle and its add-ons land atomically. The bundle owns the buy flow and hides the
      // native form that lm-addons.js normally piggybacks on, so we collect them here.
      const addonItems = this.collectAddonItems();
      if (addonItems.length) items.push(...addonItems);

      this._busy = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;

      const payload = { items: items, sections: this.gatherSectionIds() };

      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response && response.status) {
            // Shopify error payload (e.g. sold out / not enough stock).
            this.showError(response.description || response.message || 'Could not add to cart.');
            return null;
          }
          this.clearAddonChecks();
          return this.refreshAndOpenCart(response, items);
        })
        .catch(() => {
          this.showError('Something went wrong. Please try again.');
        })
        .finally(() => {
          this._busy = false;
          this.addButton.removeAttribute('aria-busy');
          this.addButton.disabled = false;
        });
    }

    refreshAndOpenCart(addResponse, items) {
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      // Fetch the cart so the drawer gets an accurate item_count (mirrors
      // product-form.js), then dispatch the event the cart drawer listens for.
      return fetch(CART_URL + '.js')
        .then((r) => r.json())
        .catch(() => null)
        .then((cart) => {
          document.dispatchEvent(
            new CartAddEvent(cart || undefined, this.id || 'lm-bundle-tiers', {
              source: 'lm-bundle-tiers',
              itemCount: totalQuantity,
              sections: addResponse && addResponse.sections,
            })
          );
        });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.removeAttribute('hidden');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
    }
  }

  customElements.define('lm-bundle-tiers', LmBundleTiers);
}
